import { useMemo, useState } from "react";
import { Edit3, Trash2 } from "lucide-react";
import {
  Iso900114001CrudTable,
  type Iso900114001TableColumn,
} from "../../../standards/iso-9001-14001/components/shared/Iso900114001CrudTable";
import {
  Iso900114001Input,
  Iso900114001Select,
} from "../../../standards/iso-9001-14001/components/shared/Iso900114001Fields";
import { Iso900114001Filters } from "../../../standards/iso-9001-14001/components/shared/Iso900114001Filters";
import {
  Iso2700127701DataTable,
  Iso2700127701Filters,
  Iso2700127701IconButton,
  Iso2700127701InputField,
  Iso2700127701SelectField,
  type Iso2700127701TableColumn,
} from "../../../standards/iso-27001-27701/components/shared";
import type {
  Indicador,
  IndicadorDataRow,
  IndicadoresStandard,
  IndicadorTableConfig,
} from "../types/indicadores";
import { IndicadorChart } from "./IndicadorChart";

const MONTH_LABELS: Record<string, string> = {
  enero: "Enero",
  febrero: "Febrero",
  marzo: "Marzo",
  abril: "Abril",
  mayo: "Mayo",
  junio: "Junio",
  julio: "Julio",
  agosto: "Agosto",
  septiembre: "Septiembre",
  octubre: "Octubre",
  noviembre: "Noviembre",
  diciembre: "Diciembre",
};

const LOADING_INDICATOR: Indicador = {
  id: 0,
  numero: 0,
  slug: "cargando",
  nombre: "Indicador",
  descripcion: null,
  criterio_1: null,
  criterio_2: null,
  usuario_modificacion_id: null,
  usuario_modificacion_nombre: null,
  created_at: null,
  updated_at: null,
};

type Props = {
  standard: IndicadoresStandard;
  indicator: Indicador | null;
  config: IndicadorTableConfig | null;
  rows: IndicadorDataRow[];
  loading?: boolean;
  eyebrow: string;
  title: string;
  createLabel: string;
  emptyText: string;
  showCharts?: boolean;
  onCreate: () => void;
  onEdit: (row: IndicadorDataRow) => void;
  onDelete: (row: IndicadorDataRow) => void;
};

export function IndicadorRecordsPanel({
  standard,
  indicator,
  config,
  rows,
  loading = false,
  eyebrow,
  title,
  createLabel,
  emptyText,
  showCharts = false,
  onCreate,
  onEdit,
  onDelete,
}: Props) {
  const [search, setSearch] = useState("");
  const [year, setYear] = useState("");
  const [series, setSeries] = useState("");
  const [visibleMonth, setVisibleMonth] = useState("");

  const effectiveConfig = useMemo<IndicadorTableConfig>(() => {
    if (config) return config;

    return {
      kind: indicator?.numero === 5 ? "electric" : "monthly_numeric",
      fields: [],
      months: Object.keys(MONTH_LABELS),
      chart: showCharts,
      annual_sum_all: false,
      annual_sum_indicators: [],
    };
  }, [config, indicator?.numero, showCharts]);

  const seriesField = useMemo(() => {
    if (effectiveConfig.kind === "electric") return null;
    if (effectiveConfig.fields.some((field) => field.name === "indicador")) return "indicador";
    if (effectiveConfig.fields.some((field) => field.name === "referencia")) return "referencia";
    return null;
  }, [effectiveConfig]);

  const years = useMemo(
    () => Array.from(
      new Set(
        rows
          .map((row) => Number(row.anio))
          .filter((value) => Number.isInteger(value)),
      ),
    ).sort((left, right) => left - right),
    [rows],
  );

  const seriesOptions = useMemo(() => {
    if (!seriesField) return [];

    return Array.from(
      new Set(
        rows
          .map((row) => String(row[seriesField] ?? "").trim())
          .filter(Boolean),
      ),
    ).sort((left, right) => left.localeCompare(right, "es"));
  }, [rows, seriesField]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase("es-ES");

    return rows.filter((row) => {
      if (year && String(row.anio) !== year) return false;
      if (series && seriesField && String(row[seriesField] ?? "") !== series) return false;
      if (!normalizedSearch) return true;

      return Object.values(row).some((value) =>
        String(value ?? "").toLocaleLowerCase("es-ES").includes(normalizedSearch),
      );
    });
  }, [rows, search, series, seriesField, year]);

  const visibleFields = useMemo(() => {
    if (!visibleMonth || effectiveConfig.kind === "electric") return effectiveConfig.fields;

    const persistentFields = new Set([
      "anio",
      "indicador",
      "criterio",
      "referencia",
      visibleMonth,
      "anual",
    ]);

    return effectiveConfig.fields.filter((field) => persistentFields.has(field.name));
  }, [effectiveConfig, visibleMonth]);

  const resetFilters = () => {
    setSearch("");
    setYear("");
    setSeries("");
    setVisibleMonth("");
  };

  const columns9001 = useMemo<Iso900114001TableColumn<IndicadorDataRow>[]>(
    () => visibleFields.map((field) => ({
      key: field.name,
      header: field.label,
      render: (row) => renderFieldValue(field.name, row[field.name]),
    })),
    [visibleFields],
  );

  const columns27001 = useMemo<Iso2700127701TableColumn<IndicadorDataRow>[]>(
    () => [
      {
        key: "acciones",
        header: "Acciones",
        className: "iso27001-actions-column",
        render: (row) => (
          <div className="iso27001-row-actions">
            <Iso2700127701IconButton
              label="Editar registro"
              icon={<Edit3 size={15} />}
              onClick={() => onEdit(row)}
            />
            <Iso2700127701IconButton
              label="Eliminar registro"
              icon={<Trash2 size={15} />}
              danger
              onClick={() => onDelete(row)}
            />
          </div>
        ),
      },
      ...visibleFields.map((field) => ({
        key: field.name,
        header: field.label,
        render: (row: IndicadorDataRow) => renderFieldValue(field.name, row[field.name]),
      })),
    ],
    [onDelete, onEdit, visibleFields],
  );

  return (
    <section className="indicadores-records-layout">
      {standard === "iso-9001-14001" ? (
        <Iso900114001Filters
          title={`Filtrar ${title.toLocaleLowerCase("es-ES")}`}
          count={filteredRows.length}
          onReset={resetFilters}
        >
          <Iso900114001Input
            label="Buscar"
            type="search"
            value={search}
            placeholder="Buscar en los datos"
            disabled={loading}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Iso900114001Select
            label="Año"
            value={year}
            disabled={loading}
            onChange={(event) => setYear(event.target.value)}
          >
            <option value="">Todos los años</option>
            {years.map((item) => <option key={item} value={item}>{item}</option>)}
          </Iso900114001Select>
          {seriesOptions.length > 0 ? (
            <Iso900114001Select
              label="Dato"
              value={series}
              disabled={loading}
              onChange={(event) => setSeries(event.target.value)}
            >
              <option value="">Todos los datos</option>
              {seriesOptions.map((item) => <option key={item} value={item}>{item}</option>)}
            </Iso900114001Select>
          ) : null}
          {effectiveConfig.kind !== "electric" ? (
            <Iso900114001Select
              label="Mes visible"
              value={visibleMonth}
              disabled={loading}
              onChange={(event) => setVisibleMonth(event.target.value)}
            >
              <option value="">Todos los meses</option>
              {effectiveConfig.months.map((month) => (
                <option key={month} value={month}>{MONTH_LABELS[month] ?? month}</option>
              ))}
            </Iso900114001Select>
          ) : null}
        </Iso900114001Filters>
      ) : (
        <Iso2700127701Filters
          title={`Filtrar ${title.toLocaleLowerCase("es-ES")}`}
          description="Acota la tabla por texto, año, dato o mes visible."
          visibleCount={filteredRows.length}
          totalCount={rows.length}
          onReset={resetFilters}
        >
          <Iso2700127701InputField
            label="Buscar"
            type="search"
            value={search}
            placeholder="Buscar en los datos"
            disabled={loading}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Iso2700127701SelectField
            label="Año"
            value={year}
            disabled={loading}
            onChange={(event) => setYear(event.target.value)}
          >
            <option value="">Todos los años</option>
            {years.map((item) => <option key={item} value={item}>{item}</option>)}
          </Iso2700127701SelectField>
          {seriesOptions.length > 0 ? (
            <Iso2700127701SelectField
              label="Dato"
              value={series}
              disabled={loading}
              onChange={(event) => setSeries(event.target.value)}
            >
              <option value="">Todos los datos</option>
              {seriesOptions.map((item) => <option key={item} value={item}>{item}</option>)}
            </Iso2700127701SelectField>
          ) : null}
          {effectiveConfig.kind !== "electric" ? (
            <Iso2700127701SelectField
              label="Mes visible"
              value={visibleMonth}
              disabled={loading}
              onChange={(event) => setVisibleMonth(event.target.value)}
            >
              <option value="">Todos los meses</option>
              {effectiveConfig.months.map((month) => (
                <option key={month} value={month}>{MONTH_LABELS[month] ?? month}</option>
              ))}
            </Iso2700127701SelectField>
          ) : null}
        </Iso2700127701Filters>
      )}

      {showCharts ? (
        <IndicadorChart
          indicator={indicator ?? LOADING_INDICATOR}
          config={effectiveConfig}
          rows={filteredRows}
          loading={loading}
          placeholderCount={indicator?.numero === 5 ? 2 : 1}
        />
      ) : null}

      {standard === "iso-9001-14001" ? (
        <Iso900114001CrudTable
          title={title}
          rows={filteredRows}
          columns={columns9001}
          getRowKey={(row) => row.id}
          loading={loading}
          error={null}
          createLabel={createLabel}
          emptyTitle={emptyText}
          onCreate={onCreate}
          onEdit={onEdit}
          onDelete={onDelete}
          onRetry={() => undefined}
          pageSize={25}
        />
      ) : (
        <Iso2700127701DataTable
          title={title}
          eyebrow={eyebrow}
          countLabel={`${filteredRows.length} registros`}
          rows={filteredRows}
          columns={columns27001}
          getRowKey={(row) => row.id}
          loading={loading}
          error={null}
          emptyTitle="Sin registros"
          emptyDescription={emptyText}
          createLabel={createLabel}
          onCreate={onCreate}
          onRetry={() => undefined}
          pageSize={25}
        />
      )}
    </section>
  );
}

function renderFieldValue(
  fieldName: string,
  value: string | number | null | undefined,
) {
  if (fieldName !== "criterio") {
    return formatValue(value);
  }

  const formatted = formatValue(value);
  const normalized = String(value ?? "").trim().toLocaleLowerCase("es-ES");
  const criterionClass = normalized === "criterio 1"
    ? "indicadores-criterio-1"
    : normalized === "criterio 2"
      ? "indicadores-criterio-2"
      : "";

  return (
    <span className={`indicadores-criterio-cell ${criterionClass}`.trim()}>
      {formatted}
    </span>
  );
}

function formatValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "-";

  if (typeof value === "number") {
    return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 10 }).format(value);
  }

  return value;
}
